// ============================================================================
// Product catalog API client — Plan 4a (read endpoints)
//
// Read endpoints shipped in Plan 4a Tasks 1-2:
//   - GET /api/product-categories          (full category tree)
//   - GET /api/product-categories?parentId={id}  (child categories)
//   - GET /api/products?categoryId={id}    (product list by category)
//   - GET /api/products/{id}               (product detail + price tiers)
//
// Write endpoints (create/update/delete categories and products) are deferred
// to Plan 4b (admin surfaces).
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";

export interface ProductCategoryNode {
  /** Stable id. Used as the React key + the `categoryId` filter on lookups. */
  id: number;
  /** Display label shown in the category tree. */
  name: string;
  /** True if this category has children — drives Task 9 subcategory dialog. */
  hasSubcategories: boolean;
  /** Optional parent reference (null for top-level). */
  parentCategoryId: number | null;
}

export interface ProductSummary {
  productId: number;
  productName: string;
  productCode: string | null;
  categoryId: number;
  shortDescription: string | null;
}

export interface ProductPriceTier {
  /** Quantity threshold (inclusive lower bound). */
  minQuantity: number;
  /** Unit price ex-GST at this tier. */
  unitPriceExGst: number;
}

export interface ProductDetail {
  productId: number;
  productName: string;
  productCode: string | null;
  description: string;
  /** Quantity-tier price table. Empty when product is single-priced. */
  priceTiers: ProductPriceTier[];
  /** Single-price fallback used when `priceTiers` is empty. */
  unitPriceExGst: number | null;
}

export interface ProductSubcategory {
  /** Stable id of the child category. Used as the React key + drilldown filter. */
  id: number;
  /** Display label shown in the subcategory dialog. */
  label: string;
}

export const productKeys = {
  categoryTree: () => ["product-categories"] as const,
  subcategories: (categoryId: number | null) =>
    ["product-subcategories", categoryId] as const,
  products: (categoryId: number | null) => ["products", categoryId] as const,
  product: (productId: number) => ["product", productId] as const,
};

/**
 * Fetch the full product-category tree.
 */
export function useProductCategoryTree() {
  return useQuery<ProductCategoryNode[]>({
    queryKey: productKeys.categoryTree(),
    queryFn: () => apiFetch<ProductCategoryNode[]>("/api/product-categories"),
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetch the child categories ("subcategories") of a parent category.
 *
 * Calls `GET /api/product-categories?parentId={categoryId}`. Disabled until
 * a non-null `categoryId` is provided. The `SelectSubcategoryDialog` caller
 * shape is unchanged.
 */
export function useSubcategories(categoryId: number | null | undefined) {
  const normalizedId = categoryId ?? null;
  return useQuery<ProductSubcategory[]>({
    queryKey: productKeys.subcategories(normalizedId),
    queryFn: () => apiFetch<ProductSubcategory[]>(`/api/product-categories?parentId=${normalizedId}`),
    enabled: normalizedId != null,
    staleTime: 60_000,
  });
}

/**
 * Fetch products under a category. Disabled until a non-null `categoryId` is
 * provided. Calls `GET /api/products?categoryId={categoryId}`.
 */
export function useProductsByCategory(categoryId: number | null) {
  return useQuery<ProductSummary[]>({
    queryKey: productKeys.products(categoryId),
    queryFn: () => apiFetch<ProductSummary[]>(`/api/products?categoryId=${categoryId}`),
    enabled: categoryId != null,
    staleTime: 60_000,
  });
}

/**
 * Fetch a single product with description + price tiers.
 * Calls `GET /api/products/{productId}`. Disabled until a non-null
 * `productId` is provided.
 */
export function useProduct(productId: number | null) {
  return useQuery<ProductDetail | null>({
    queryKey: productKeys.product(productId ?? 0),
    queryFn: () => apiFetch<ProductDetail | null>(`/api/products/${productId}`),
    enabled: productId != null,
    staleTime: 60_000,
  });
}
